import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Skeleton } from '@epoch-ui/components/Skeleton/skeleton';
import { HorizontalBarChart } from '@event-editor/components/QuestionResponsesCharts/HorizontalBarChart';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { eventRegistrationDataApi } from '@event-editor/api/registration/getEditEventRegistrationData';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';

import {
  NoDataAvailable,
  NoRegistrationsYet,
  RegistrationWasReset
} from '../components/EmptyRegistrationStates';

import { formatRegistrationsData } from './utils/formatRegistrationsData';

export const RegistrationsByPlatform = (): React.ReactElement => {
  const event = useEditableEvent();
  const { id, datetimeScheduledChanged, createdAt } = event;

  // This is done because for events before the release, we might have inaccurate data
  const isLegacyEvent = createdAt
    ? new Date(createdAt).getTime() < new Date('2023-09-05').getTime()
    : false;

  const { data, isLoading, error } = useQuery({
    queryKey: [...eventEditorQueryKeys.event(Number(id)), 'registrationsByPlatform'],
    queryFn: () => eventRegistrationDataApi.registrationsByPlatform(Number(id)),
    enabled: !isLegacyEvent && !!id
  });

  const formattedData = useMemo(() => {
    if (!data) return [];
    return formatRegistrationsData(data);
  }, [data]);

  const content = useMemo(() => {
    if (isLegacyEvent) {
      return <NoDataAvailable />;
    }

    if (isLoading) {
      return (
        <div className="tw-space-y-6 tw-py-4">
          <Skeleton className="tw-h-10 tw-w-full tw-rounded-md" />
          <Skeleton className="tw-h-10 tw-w-4/5 tw-rounded-md" />
          <Skeleton className="tw-h-10 tw-w-3/5 tw-rounded-md" />
        </div>
      );
    }

    if (error) {
      console.error('Failed to fetch registrations by platform:', error);
      return <NoDataAvailable />;
    }

    if (datetimeScheduledChanged) {
      return <RegistrationWasReset />;
    }

    if (formattedData.length === 0) {
      return <NoRegistrationsYet />;
    }

    return (
      <div className="tw-h-[300px] tw-rounded-[16px] tw-border tw-border-solid tw-border-border-grey tw-bg-background tw-p-6">
        <HorizontalBarChart data={formattedData} />
      </div>
    );
  }, [isLegacyEvent, isLoading, error, datetimeScheduledChanged, formattedData]);

  return (
    <div>
      <div className="tw-flex tw-items-center tw-justify-between">
        <h2 className="tw-m-0 tw-mb-4 tw-text-xl tw-font-medium tw-leading-7">
          Registrations by platform
        </h2>
      </div>
      {content}
    </div>
  );
};
